import { ApolloError, useQuery } from '@apollo/client';
import { usePersistedCacheQuery } from '@common/apollo';
import {
    AllBudgetCategoryGroupsDocument,
    BudgetCategoryGroupFieldsFragment,
    TransactionEntriesFilteredDocument,
    TransactionEntryCategoryGroupFieldsFragment,
} from '@common/graphql/generated';
import {
    CategoryGroupWithSpending,
    useAllCategoryGroupsWithSpending,
} from '@common/react/budget';
import {
    CashflowTransactionEntry,
    useFinancialEntityMap,
    useTransactionEntries,
    useTransactionEntryCategories,
} from '@common/react/cashflow';
import { useTransactionEntryCategoryGroups } from '@common/react/cashflow/useTransactionEntryCategoryGroups';
import { DateRange } from '@common/types';
import {
    DateRangeType,
    DEFAULT_DATE_RANGE_TYPE,
    tickRangeFromDateRangeType,
} from '@common/util/dateTime';
import { isNullish } from '@common/util/types';
import { createContext, useContext, useState } from 'react';

interface BudgetContext {
    transactionEntries: CashflowTransactionEntry[];
    transactionEntryCategoryGroups: TransactionEntryCategoryGroupFieldsFragment[];
    budgetCategoryGroups: BudgetCategoryGroupFieldsFragment[];
    categoryGroupsWithSpending: CategoryGroupWithSpending[];
    loading: boolean;
    error: ApolloError | undefined;
    dateRangeType: DateRangeType;
    setDateRangeType: (dateRangeType: DateRangeType) => void;
    dateRange: DateRange;
}

const Ctx = createContext<BudgetContext>({
    transactionEntries: [],
    transactionEntryCategoryGroups: [],
    budgetCategoryGroups: [],
    categoryGroupsWithSpending: [],
    loading: false,
    error: undefined,
    dateRange: { startDate: '', endDate: '' },
    dateRangeType: DEFAULT_DATE_RANGE_TYPE,
    setDateRangeType: () => {},
});

export const BudgetProvider = ({
    children,
    initialDateRangeType,
}: {
    children: React.ReactNode;
    initialDateRangeType?: DateRangeType;
}) => {
    const [dateRangeType, setDateRangeType] = useState<DateRangeType>(
        initialDateRangeType || DEFAULT_DATE_RANGE_TYPE
    );
    const [dateRange, setDateRange] = useState<DateRange>(
        tickRangeFromDateRangeType(dateRangeType)
    );

    const updateDateRangeType = (newDateRangeType: DateRangeType) => {
        setDateRangeType(newDateRangeType);
        setDateRange(tickRangeFromDateRangeType(newDateRangeType));
    };

    const {
        data: transactionEntriesQuery,
        error,
        loading: transactionEntriesLoading,
    } = usePersistedCacheQuery(TransactionEntriesFilteredDocument, {
        variables: {
            filter: {
                isHidden: { equalTo: false },
                date: {
                    greaterThanOrEqualTo: dateRange.startDate,
                    lessThanOrEqualTo: dateRange.endDate,
                },
            },
        },
        fetchPolicy: 'cache-and-network',
    });

    const {
        transactionEntryCategoriesMapById,
        transactionEntryCategoriesLoading,
    } = useTransactionEntryCategories();

    const { financialEntityMap, financialEntityMapLoading } =
        useFinancialEntityMap();

    const {
        transactionEntryCategoryGroups,
        transactionEntryCategoryGroupsLoading,
    } = useTransactionEntryCategoryGroups();

    const {
        data: budgetCategoryGroupsData,
        loading: budgetCategoryGroupsLoading,
    } = useQuery(AllBudgetCategoryGroupsDocument);

    const transactionEntries = useTransactionEntries({
        transactionEntries: transactionEntriesQuery?.transactionEntries,
        financialEntityMap,
        transactionEntryCategoriesMapById,
    });

    const visibleTransactionEntries = transactionEntries.filter(
        // Handle case where transaction has been hidden/deleted but new data hasn't been fetched
        (t) => !t.isHidden && isNullish(t.deletedAt)
    );

    const categoryGroupsWithSpending = useAllCategoryGroupsWithSpending({
        budgetCategoryGroups:
            budgetCategoryGroupsData?.budgetCategoryGroups || [],
        transactionEntries: visibleTransactionEntries,
        transactionEntryCategoryGroups: transactionEntryCategoryGroups || [],
        dateRange,
    });

    return (
        <Ctx.Provider
            value={{
                transactionEntries: visibleTransactionEntries,
                transactionEntryCategoryGroups:
                    transactionEntryCategoryGroups || [],
                budgetCategoryGroups:
                    budgetCategoryGroupsData?.budgetCategoryGroups || [],
                categoryGroupsWithSpending,
                loading:
                    financialEntityMapLoading ||
                    transactionEntriesLoading ||
                    transactionEntryCategoryGroupsLoading ||
                    transactionEntryCategoriesLoading ||
                    budgetCategoryGroupsLoading,
                error,
                dateRange,
                dateRangeType,
                setDateRangeType: updateDateRangeType,
            }}
        >
            {children}
        </Ctx.Provider>
    );
};

export const useBudgetContext = (): BudgetContext => {
    return useContext(Ctx);
};
